import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { translate, TranslocoModule } from '@ngneat/transloco';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { combineLatest, firstValueFrom, map } from 'rxjs';

import { getAllLanguages, getEditableVrcAudiosByRole, SupportedLanguages } from '@pwp-common';

import { makePTableCols } from '../../../../common/p-table/make-p-table-cols';
import { CallEntrypointsService } from '../../../../services/call/call-entrypoints/call-entrypoints.service';
import { CallListService } from '../../../../services/call/call-list/call-list.service';
import { IvrService } from '../../../../services/call/ivr/ivr.service';
import { ServiceExceptionService } from '../../../../services/communication/service-exception/service-exception.service';
import { AdminRolesService } from '../../../../services/user/admin-roles/admin-roles.service';
import { RolesService } from '../../../../services/user/roles/roles.service';
import { VRCAudioMetadataService } from '../../../../services/vrc-audio/vrc-audio-metadata/vrc-audio-metadata.service';
import { DataTableModule } from '../../../generic/data-table/data-table.module';
import { AudioFilesEditorComponent } from '../editor/audio-files-editor/audio-files-editor.component';
import { createAudioFilesEditorRow } from '../helpers/create-audio-files-editor-row/create-audio-files-editor-row';
import { AudioFilesEditorRow } from '../helpers/create-audio-files-editor-row/interfaces';

@Component({
  selector: 'app-audio-files-table-editor',
  standalone: true,
  imports: [CommonModule, DataTableModule, TranslocoModule, ButtonModule, MatIconModule],
  templateUrl: './audio-files-table-editor.component.html',
  styleUrls: ['./audio-files-table-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DialogService],
})
export class AudioFilesTableEditorComponent {
  ///////////////////////////////////////////////////////////////////////
  // Services
  ///////////////////////////////////////////////////////////////////////
  private readonly vrcAudioMetadataService = inject(VRCAudioMetadataService);

  private readonly adminRolesService = inject(AdminRolesService);

  private readonly rolesService = inject(RolesService);

  private readonly ivrService = inject(IvrService);

  private readonly callEntrypointsService = inject(CallEntrypointsService);

  private readonly callListService = inject(CallListService);

  private readonly serviceExceptionService = inject(ServiceExceptionService);

  private readonly dialogService = inject(DialogService);

  ///////////////////////////////////////////////////////////////////////
  // Variables
  ///////////////////////////////////////////////////////////////////////

  public readonly cols = makePTableCols({
    columns: ['displayName', 'description', 'edit'],
    translationScope: 'audio-files-table-editor',
  });

  private readonly adminRoles$ = this.adminRolesService.getAdminRoles();

  private readonly roles$ = this.rolesService.getRoles();

  private readonly ivrs$ = this.ivrService.getDocsArray();

  public readonly canCreateAudioFile$ = combineLatest([this.adminRoles$, this.roles$]).pipe(
    map(([adminRoles, roles]) => adminRoles.isPwpAdmin() || roles.isOrgAdmin()),
  );

  private readonly availableLanguages$ = combineLatest([
    this.ivrs$,
    this.callEntrypointsService.getDocsArray(),
    this.callListService.getDocsArray(),
    this.serviceExceptionService.getDocsArray(),
  ]).pipe(
    map(([ivrs, callEntrypoints, callLists, serviceExceptions]) =>
      getAllLanguages({ ivrs, callEntrypoints, callLists, serviceExceptions }),
    ),
    map((languages) => (languages.length > 0 ? languages : [SupportedLanguages.en.getShortCode()])),
  );

  ///////////////////////////////////////////////////////////////////////
  // Table Data
  ///////////////////////////////////////////////////////////////////////

  public readonly tableRows$ = combineLatest([
    this.vrcAudioMetadataService.getDocsArray({ takeOne: false }),
    this.adminRoles$,
    this.roles$,
    this.ivrs$,
  ]).pipe(
    map(([vrcAudios, adminRoles, roles, ivrs]) =>
      getEditableVrcAudiosByRole({ adminRoles, roles, vrcAudios, ivrs }).map(createAudioFilesEditorRow),
    ),
  );

  ///////////////////////////////////////////////////////////////////////
  // Dialog Actions
  ///////////////////////////////////////////////////////////////////////

  private async openAudioDialog(audioId: string, headerKey: 'editDialogHeader' | 'createDialogHeader') {
    const availableLanguages = await firstValueFrom(this.availableLanguages$);

    this.dialogService.open(AudioFilesEditorComponent, {
      header: translate(`audio-files-table-editor.${headerKey}`),
      width: 'clamp(32rem, 70%, 60rem)',
      closeOnEscape: false,
      dismissableMask: false,
      data: {
        audioId,
        availableLanguages,
      },
    });
  }

  public async openNewAudioDialog() {
    await this.openAudioDialog(this.vrcAudioMetadataService.createId(), 'createDialogHeader');
  }

  public async openEditAudioDialog(rowData: AudioFilesEditorRow) {
    await this.openAudioDialog(rowData.id, 'editDialogHeader');
  }
}
